import type {
  Booking,
  BookingStatus,
  CompanyDocStatus,
  CompanyPricing,
  Driver,
  DriverDoc,
  CompanyProfile,
  DriverDocType,
  Listing,
  PublishStatus,
  Unit,
} from "../types/company";
import type { ListingDraft } from "./company";
import type { BusinessType, CompanyDocumentKey, OwnerType } from "../types/vtc";
import { CATEGORIES, ENERGIES, TRANSMISSIONS } from "../data/company";
import { REQUIRED_COMPANY_DOCS } from "../data/auth";
import { api } from "./api";

/** The company document as GET/PUT /company/profile return it. */
interface ApiCompany {
  name: string;
  siret: string;
  vat?: string;
  address: string;
  city: string;
  postalCode: string;
  region: string;
  country: string;
  firstName?: string;
  lastName?: string;
  contact: string;
  phone: string;
  email: string;
  verification: "pending" | "verified" | "rejected";
  verificationSubmittedAt?: string;
  reviewNote?: string;
  ownerType?: OwnerType;
  businessType?: BusinessType;
  requiredDocuments?: CompanyDocumentKey[];
  documents?: Partial<
    Record<CompanyDocumentKey, { status: Exclude<CompanyDocStatus, "missing">; originalName?: string }>
  >;
  createdAt?: string;
}

/** Fields PUT /company/profile accepts. */
export type CompanyProfileUpdate = Partial<
  Pick<
    CompanyProfile,
    | "name" | "siret" | "vat" | "address" | "city" | "postalCode"
    | "region" | "country" | "firstName" | "lastName" | "phone" | "email" | "businessType"
  >
>;

/** Companies created before the name split only have `contact`: "Samir Haddou" → Samir / Haddou. */
const splitName = (full: string) => {
  const [first = "", ...rest] = full.trim().split(/\s+/);
  return { firstName: first, lastName: rest.join(" ") };
};

const toProfile = (c: ApiCompany): CompanyProfile => {
  const businessType = c.businessType ?? "";
  const required = c.requiredDocuments ?? (businessType ? REQUIRED_COMPANY_DOCS[businessType] : []);
  return {
    name: c.name,
    siret: c.siret ?? "",
    vat: c.vat ?? "",
    address: c.address ?? "",
    city: c.city ?? "",
    postalCode: c.postalCode ?? "",
    region: c.region ?? "",
    country: c.country ?? "",
    ...(c.firstName || c.lastName
      ? { firstName: c.firstName ?? "", lastName: c.lastName ?? "" }
      : splitName(c.contact)),
    contact: c.contact,
    phone: c.phone,
    email: c.email,
    // "pending" but never submitted = still adding documents ("Verification to complete")
    verification:
      c.verification === "pending" && !c.verificationSubmittedAt ? "incomplete" : c.verification,
    docs: required.map((key) => {
      const doc = c.documents?.[key];
      return doc ? { key, status: doc.status, fileName: doc.originalName } : { key, status: "missing" };
    }),
    ownerType: c.ownerType ?? "company",
    businessType,
    createdAt: c.createdAt,
    submittedAt: c.verificationSubmittedAt,
    reviewNote: c.reviewNote,
  };
};

/** GET /company/profile */
export async function getCompanyProfile(): Promise<CompanyProfile> {
  const { data } = await api.get<ApiCompany>("/company/profile");
  return toProfile(data);
}

/** PUT /company/profile — returns the saved profile and the backend's confirmation. */
export async function updateCompanyProfile(
  changes: CompanyProfileUpdate,
): Promise<{ profile: CompanyProfile; message: string }> {
  const { data } = await api.put<{ message?: string; company?: ApiCompany }>(
    "/company/profile",
    changes,
  );
  // Older backends reply without the saved company: read it back instead.
  const company = data.company ?? (await api.get<ApiCompany>("/company/profile")).data;
  return { profile: toProfile(company), message: data.message ?? "" };
}

/** PUT /company/documents — uploads or replaces one verification document. */
export async function uploadCompanyDocument(key: CompanyDocumentKey, file: File): Promise<string> {
  const form = new FormData();
  form.append(key, file);
  const { data } = await api.put<{ message: string }>("/company/documents", form);
  return data.message;
}

/** POST /company/verification/submit — sends the file to the reviewers. */
export async function submitVerification(): Promise<{ profile: CompanyProfile; message: string }> {
  const { data } = await api.post<{ message: string; company: ApiCompany }>(
    "/company/verification/submit",
  );
  return { profile: toProfile(data.company), message: data.message };
}

export type ConvertFields = Required<
  Pick<CompanyProfileUpdate, "name" | "siret" | "vat" | "address" | "city" | "postalCode" | "region" | "country">
> & { businessType: BusinessType };

/**
 * POST /company/convert — individual → company, multipart with the company
 * details and the documents of the new business type (sent for review when complete).
 */
export async function convertToCompany(
  fields: ConvertFields,
  documents: Partial<Record<CompanyDocumentKey, File>>,
): Promise<{ profile: CompanyProfile; message: string }> {
  const form = new FormData();
  Object.entries(fields).forEach(([key, value]) => form.append(key, value));
  Object.entries(documents).forEach(([key, file]) => file && form.append(key, file));
  const { data } = await api.post<{ message: string; company: ApiCompany }>("/company/convert", form);
  return { profile: toProfile(data.company), message: data.message };
}

/* ---------- Listings ---------- */

/** Backend option values are slugs: "Plug-in hybrid" → "plug-in-hybrid". */
const slug = (v: string) => v.trim().toLowerCase().replace(/\s+/g, "-");
/** Back from a slug to the label the form uses (unknown values pass through). */
export const unslug = (value: string, labels: string[]) =>
  labels.find((l) => slug(l) === value) ?? value;

const DOC_TO_API: Record<DriverDocType, string> = {
  vtc: "vtcCard",
  licence: "drivingLicence",
  id: "idDocument",
  address: "proofOfAddress",
};
export const DOC_FROM_API = Object.fromEntries(
  Object.entries(DOC_TO_API).map(([k, v]) => [v, k as DriverDocType]),
);

interface ApiVehicle {
  _id: string;
  code: string;
  plate?: string;
  status: "available" | "rented" | "maintenance" | "inactive";
}

/** A listing as GET/POST/PUT/PATCH /company/listings return it. */
interface ApiListing {
  _id: string;
  brand: string;
  model: string;
  version?: string;
  year?: number;
  energy: string;
  transmission: string;
  category: string;
  city: string;
  street?: string;
  postalCode?: string;
  country?: string;
  seats: number;
  colour?: string;
  mileage?: number;
  description?: string;
  daily?: number;
  weekly: number;
  monthly: number;
  deposit: number;
  minDays: number;
  kmIncluded: number;
  extraKm: number;
  insurance?: string;
  maintenance?: string;
  requiredDocs: string[];
  photos: string[];
  status: PublishStatus;
  vehicles?: ApiVehicle[];
  sponsoredUntil?: string;
  sponsored?: boolean;
}

const UNIT_STATUS: Record<ApiVehicle["status"], Unit["status"]> = {
  available: "active",
  rented: "active",
  maintenance: "maintenance",
  inactive: "retired",
};

function fromApi(l: ApiListing): { listing: Listing; units: Unit[] } {
  const listing: Listing = {
    id: l._id,
    brand: l.brand,
    model: l.model,
    version: l.version ?? "",
    year: l.year ?? 0,
    energy: unslug(l.energy, ENERGIES) as Listing["energy"],
    transmission: unslug(l.transmission, TRANSMISSIONS) as Listing["transmission"],
    category: unslug(l.category, CATEGORIES),
    city: l.city,
    street: l.street ?? "",
    postalCode: l.postalCode ?? "",
    country: l.country ?? "",
    seats: l.seats,
    colour: l.colour ?? "",
    mileage: l.mileage ?? 0,
    description: l.description ?? "",
    daily: l.daily,
    weekly: l.weekly,
    monthly: l.monthly,
    deposit: l.deposit,
    minDays: l.minDays,
    kmIncluded: l.kmIncluded,
    extraKm: l.extraKm,
    insurance: l.insurance ?? "",
    maintenance: l.maintenance ?? "",
    requiredDocs: l.requiredDocs.map((d) => DOC_FROM_API[d]).filter(Boolean),
    photos: Array.from({ length: 5 }, (_, i) => i < l.photos.length),
    photoUrls: l.photos,
    publish: l.status,
    // Only a running sponsorship ("YYYY-MM-DD", like the rest of the store)
    ...(l.sponsored && l.sponsoredUntil && { urgentUntil: l.sponsoredUntil.slice(0, 10) }),
  };
  const units: Unit[] = (l.vehicles ?? []).map((v) => ({
    id: v._id,
    listingId: l._id,
    code: v.code,
    plate: v.plate ?? "",
    mileage: listing.mileage,
    status: UNIT_STATUS[v.status] ?? "active",
    blocks: [],
  }));
  return { listing, units };
}

type ListingWithUnits = ReturnType<typeof fromApi>;
type Saved = ListingWithUnits & { message: string };

/**
 * GET /company/listings, then GET /company/listings/:id for each one —
 * the list only carries a vehicle count, and availability needs the vehicles.
 */
export async function fetchListings(): Promise<{ listings: Listing[]; units: Unit[] }> {
  const { data } = await api.get<ApiListing[]>("/company/listings");
  const full = await Promise.all(data.map((l) => getListing(l._id)));
  return { listings: full.map((f) => f.listing), units: full.flatMap((f) => f.units) };
}

/** GET /company/listings/:id */
export async function getListing(id: string): Promise<ListingWithUnits> {
  const { data } = await api.get<ApiListing>(`/company/listings/${id}`);
  return fromApi(data);
}

export type ListingFields = Omit<ListingDraft, "id" | "photos" | "photoUrls">;

/** A physical vehicle row: `_id` for existing ones, none for new ones. */
export type UnitRow = { _id?: string; code: string; plate: string };

/** Multipart body shared by create and update. */
function listingForm(fields?: ListingFields, photos: File[] = [], units?: UnitRow[]): FormData {
  const form = new FormData();
  if (fields) {
    const { requiredDocs, energy, transmission, category, ...rest } = fields;
    Object.entries(rest).forEach(([key, value]) => {
      // A blank year reaches here as 0; leave it out rather than fail validation.
      if (!(key === "year" && !value)) form.append(key, String(value));
    });
    form.append("energy", slug(energy));
    form.append("transmission", slug(transmission));
    form.append("category", slug(category));
    form.append("requiredDocs", JSON.stringify(requiredDocs.map((d) => DOC_TO_API[d])));
  }
  if (units) form.append("units", JSON.stringify(units));
  photos.forEach((file) => form.append("photos", file));
  return form;
}

/**
 * POST /company/listings — multipart: the form fields, `photos` files (max 5,
 * first = main photo) and `units` as a JSON string.
 */
export async function createListing(
  fields: ListingFields,
  photos: File[],
  units: UnitRow[],
): Promise<Saved> {
  const { data } = await api.post<{ message: string; listing: ApiListing }>(
    "/company/listings",
    listingForm(fields, photos, units),
  );
  return { ...fromApi(data.listing), message: data.message };
}

/**
 * PUT /company/listings/:id — only what is passed is changed.
 * `keepPhotos`: existing photo URLs to keep, in order; new `photos` are appended.
 * `units`: the full list of vehicles; missing ones are removed.
 */
export async function updateListing(
  id: string,
  changes: { fields?: ListingFields; keepPhotos?: string[]; photos?: File[]; units?: UnitRow[] },
): Promise<Saved> {
  const form = listingForm(changes.fields, changes.photos, changes.units);
  if (changes.keepPhotos) form.append("keepPhotos", JSON.stringify(changes.keepPhotos));
  const { data } = await api.put<{ message: string; listing: ApiListing }>(
    `/company/listings/${id}`,
    form,
  );
  return { ...fromApi(data.listing), message: data.message };
}

/** PATCH /company/listings/:id/status */
export async function setListingStatus(
  id: string,
  status: Exclude<PublishStatus, "moderated">,
): Promise<Saved> {
  const { data } = await api.patch<{ message: string; listing: ApiListing }>(
    `/company/listings/${id}/status`,
    { status },
  );
  return { ...fromApi(data.listing), message: data.message };
}

/** DELETE /company/listings/:id — drafts and archived listings only. */
export async function deleteListing(id: string): Promise<string> {
  const { data } = await api.delete<{ message: string }>(`/company/listings/${id}`);
  return data.message;
}

/* ---------- Subscription and sponsored listings ---------- */

interface ApiSubscription {
  mode: "launch" | "paid";
  launch: { enabled: boolean; listingLimit: number | null; noticeDays: number };
  listingLimit: number | null;
  activeListings: number;
  sponsor: { enabled: boolean; options: { days: number; priceCents: number }[] };
  plans?: { id: string; name: string; priceCents: number; listingLimit: number; features: string[] }[];
}

/** GET /company/subscription — prices come in cents, the store uses euros. */
export async function getSubscription(): Promise<CompanyPricing> {
  const { data } = await api.get<ApiSubscription>("/company/subscription");
  return {
    mode: data.mode,
    listingLimit: data.listingLimit,
    noticeDays: data.launch.noticeDays,
    activeListings: data.activeListings,
    sponsor: {
      enabled: data.sponsor.enabled,
      options: data.sponsor.options.map((o) => ({ days: o.days, price: o.priceCents / 100 })),
    },
    plans: data.plans?.map((p) => ({ ...p, price: p.priceCents / 100 })),
  };
}

/** POST /company/listings/:id/sponsor/checkout — returns the Stripe Checkout page URL. */
export async function startSponsorCheckout(id: string, days: number): Promise<string> {
  const { data } = await api.post<{ url: string }>(`/company/listings/${id}/sponsor/checkout`, { days });
  return data.url;
}

/**
 * GET /company/sponsor/checkout/:sessionId — back from Stripe: activates the
 * sponsorship once paid. `urgentUntil` is the listing's new end date.
 */
export async function confirmSponsorCheckout(
  sessionId: string,
): Promise<{ status: "paid" | "pending" | "expired"; message: string; listingId?: string; urgentUntil?: string }> {
  const { data } = await api.get<{ status: "paid" | "pending" | "expired"; message: string; listing?: ApiListing }>(
    `/company/sponsor/checkout/${encodeURIComponent(sessionId)}`,
  );
  const listing = data.listing && fromApi(data.listing).listing;
  return { status: data.status, message: data.message, listingId: listing?.id, urgentUntil: listing?.urgentUntil };
}

/* ---------- Bookings and messages ---------- */

interface ApiBooking {
  id: string;
  ref: string;
  status: BookingStatus;
  start: string;
  end: string;
  note: string;
  terms: { daily?: number; weekly: number; monthly: number; deposit: number; kmIncluded: number; minDays?: number; estimate?: number };
  declineReason: string;
  createdAt: string;
  listing: { id: string; brand: string; model: string } | null;
  vehicle: { id: string; code: string; plate: string } | null;
  unread: number;
  driver?: {
    id: string;
    firstName: string;
    lastName: string;
    city: string;
    licenceYear: number | null;
    createdAt?: string;
    verified: boolean;
    docs: { type: string; status: string }[];
    phone?: string;
    email?: string;
  };
  messages?: { from: "company" | "driver" | "system"; text: string; at: string }[];
  history?: { status: BookingStatus; by: string; at: string }[];
}

/** API document statuses -> the company area's 4 states. */
const DOC_STATUS: Record<string, DriverDoc["status"]> = {
  approved: "approved",
  expiring_soon: "approved",
  under_review: "pending",
  rejected: "missing",
  missing: "missing",
  expired: "expired",
};

/** One API booking -> the store's booking (keyed by its reference) and its driver. */
function bookingFromApi(b: ApiBooking): { booking: Booking; driver: Driver | null } {
  const booking: Booking = {
    id: b.ref,
    listingId: b.listing?.id ?? "",
    driverId: b.driver?.id ?? "",
    unitId: b.vehicle?.id ?? null,
    start: b.start,
    end: b.end,
    createdAt: b.createdAt,
    status: b.status,
    note: b.note || undefined,
    terms: {
      daily: b.terms.daily,
      weekly: b.terms.weekly,
      monthly: b.terms.monthly,
      deposit: b.terms.deposit,
      kmIncluded: b.terms.kmIncluded,
      estimate: b.terms.estimate,
    },
    messages: b.messages ?? [],
    history: (b.history ?? []).map((h) => ({ status: h.status, by: h.by, at: h.at })),
    unread: b.unread,
    declineReason: b.declineReason || undefined,
  };
  const d = b.driver;
  const driver: Driver | null = d
    ? {
        id: d.id,
        firstName: d.firstName,
        lastName: d.lastName,
        city: d.city,
        email: d.email ?? "",
        phone: d.phone ?? "",
        createdAt: d.createdAt ?? b.createdAt,
        licenceYear: d.licenceYear ?? 0,
        status: d.verified ? "verified" : "pending",
        docs: d.docs
          .map((x) => ({ type: DOC_FROM_API[x.type], status: DOC_STATUS[x.status] ?? "missing" }))
          .filter((x) => x.type),
      }
    : null;
  return { booking, driver };
}

export type StoreBooking = ReturnType<typeof bookingFromApi>;

/** GET /company/bookings?full=true — every booking with its messages. */
export async function fetchBookings(): Promise<StoreBooking[]> {
  const { data } = await api.get<{ items: ApiBooking[] }>("/company/bookings", { params: { full: "true" } });
  return data.items.map(bookingFromApi);
}

/** GET /company/bookings/:ref — also marks the conversation as read. */
export async function openBooking(ref: string): Promise<StoreBooking> {
  const { data } = await api.get<ApiBooking>(`/company/bookings/${encodeURIComponent(ref)}`);
  return bookingFromApi(data);
}

export type BookingAction = "accept" | "assign" | "decline" | "request-documents" | "start" | "complete";

/** POST /company/bookings/:ref/{action} */
export async function bookingAction(ref: string, action: BookingAction, body: Record<string, unknown> = {}): Promise<StoreBooking> {
  const { data } = await api.post<ApiBooking>(`/company/bookings/${encodeURIComponent(ref)}/${action}`, body);
  return bookingFromApi(data);
}

/** POST /company/bookings/:ref/messages */
export async function sendBookingMessage(ref: string, text: string): Promise<StoreBooking> {
  const { data } = await api.post<ApiBooking>(`/company/bookings/${encodeURIComponent(ref)}/messages`, { text });
  return bookingFromApi(data);
}

/** GET /company/summary — sidebar badges. */
export async function getCompanySummary(): Promise<{ pendingBookings: number; unreadMessages: number; newReviews: number }> {
  const { data } = await api.get("/company/summary");
  return data;
}
